/**
 * Validation Module (Zod Powered)
 * Handles form validation using Zod schemas and Bootstrap UI.
 */
import { z } from 'zod';

export default class Validation {
    /**
     * Creates a new Validation instance.
     * @param {string|HTMLFormElement} target - CSS selector or Form element.
     * @param {Object} options - Configuration options.
     */
    constructor(target, options = {}) {
        this.options = {
            schema: null,
            onValidationSuccess: (data, form) => {
                form.submit();
            },
            onValidationError: (error, form) => {
                // Default error logging
            },
            ...options
        };

        this.events = {};
        
        this.forms = typeof target === 'string' 
            ? Array.from(document.querySelectorAll(target)) 
            : (target instanceof HTMLFormElement ? [target] : []);

        if (this.forms.length === 0) return;

        this.init();
    }

    /**
     * Registers an event listener.
     * @param {string} event - 'validationSuccess' or 'validationError'
     * @param {Function} callback 
     */
    on(event, callback) {
        if (!this.events[event]) this.events[event] = [];
        this.events[event].push(callback);
        return this; // For chaining
    }

    /**
     * Triggers an event.
     * @param {string} event 
     * @param  {...any} args 
     */
    trigger(event, ...args) {
        if (this.events[event]) {
            this.events[event].forEach(callback => callback(...args));
        }
    }

    /**
     * Initializes validation for all matched forms.
     */
    init() {
        this.forms.forEach(form => this.setupForm(form));
    }

    /**
     * Sets up validation for a single form.
     * @param {HTMLFormElement} form 
     */
    setupForm(form) {
        form.setAttribute('novalidate', '');

        form.addEventListener('submit', (event) => {
            event.preventDefault();
            this.clearErrors(form);

            const formData = new FormData(form);
            const data = Object.fromEntries(formData.entries());

            const finalSchema = this.buildSchema(form, this.options.schema);
            const result = finalSchema.safeParse(data);

            if (!result.success) {
                event.stopPropagation();
                this.displayErrors(form, result.error);
                form.classList.add('was-validated');
                
                this.trigger('validationError', result.error, form);
                if (typeof this.options.onValidationError === 'function') {
                    this.options.onValidationError(result.error, form);
                }
            } else {
                form.classList.remove('was-validated');
                
                this.trigger('validationSuccess', result.data, form);
                if (typeof this.options.onValidationSuccess === 'function') {
                    this.options.onValidationSuccess(result.data, form);
                }
            }
        });

        form.querySelectorAll('input, select, textarea').forEach(input => {
            const handleValidation = () => {
                if (form.classList.contains('was-validated')) {
                    this.validateField(form, input);
                }
            };
            input.addEventListener('input', handleValidation);
            input.addEventListener('blur', handleValidation);
        });
    }

    /**
     * Validates a single field and updates its UI.
     */
    validateField(form, field) {
        const name = field.getAttribute('name');
        if (!name) return;

        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        
        const schema = this.buildSchema(form, this.options.schema);
        const result = schema.safeParse(data);

        field.classList.remove('is-invalid');
        const container = field.closest('.has-validation') || field.parentElement;
        const feedbacks = container.querySelectorAll('.invalid-feedback');
        feedbacks.forEach(fb => fb.style.setProperty('display', 'none', 'important'));

        if (!result.success) {
            const fieldIssues = result.error.issues.filter(issue => issue.path[0] === name);
            if (fieldIssues.length > 0) {
                this.displayErrors(form, { issues: fieldIssues });
            }
        }
    }

    /**
     * Dynamically builds a Zod schema.
     */
    buildSchema(form, schema) {
        let shape = {};

        form.querySelectorAll('input, select, textarea').forEach(input => {
            const name = input.getAttribute('name');
            if (!name) return;
            
            // If we've already defined a schema for this name (e.g. for radio groups), skip
            if (shape[name] && (input.type === 'radio' || input.type === 'checkbox')) return;

            let fieldSchema;
            
            // 1. Mapping by Type
            switch (input.type) {
                case 'email':
                    fieldSchema = z.email({ message: "email" });
                    break;
                case 'url':
                    fieldSchema = z.url({ message: "url" });
                    break;
                case 'number':
                case 'range':
                    fieldSchema = z.coerce.number({ invalid_type_error: "number" });
                    if (input.hasAttribute('min')) fieldSchema = fieldSchema.min(Number(input.getAttribute('min')), { message: "min" });
                    if (input.hasAttribute('max')) fieldSchema = fieldSchema.max(Number(input.getAttribute('max')), { message: "max" });
                    break;
                case 'checkbox':
                    if (input.hasAttribute('required')) {
                        fieldSchema = z.literal("on", { errorMap: () => ({ message: "required" }) });
                    } else {
                        fieldSchema = z.string().optional();
                    }
                    break;
                case 'radio':
                    // For radio, we just check if any in the group is required
                    const group = form.querySelectorAll(`input[type="radio"][name="${name}"]`);
                    const isRequired = Array.from(group).some(r => r.hasAttribute('required'));
                    fieldSchema = z.string();
                    if (isRequired) {
                        fieldSchema = fieldSchema.min(1, { message: "required" });
                    } else {
                        fieldSchema = fieldSchema.optional().or(z.literal(''));
                    }
                    break;
                default:
                    fieldSchema = z.string();
            }

            // 2. Handle Pattern
            if (input.hasAttribute('pattern') && input.type !== 'checkbox' && input.type !== 'radio') {
                const regex = new RegExp(input.getAttribute('pattern'));
                fieldSchema = z.string().regex(regex, { message: "pattern" }).pipe(fieldSchema);
            }

            // 3. Handle Required for other types (Strings)
            if (input.hasAttribute('required') && input.type !== 'checkbox' && input.type !== 'radio') {
                fieldSchema = z.string().min(1, { message: "required" }).pipe(fieldSchema);
            } else if (!input.hasAttribute('required') && input.type !== 'checkbox' && input.type !== 'radio') {
                fieldSchema = fieldSchema.optional().or(z.literal(''));
            }

            shape[name] = fieldSchema;
        });

        return schema ? z.object(shape).merge(schema) : z.object(shape);
    }

    /**
     * Maps a Zod issue to a string error type.
     */
    getErrorType(issue) {
        if (issue.message === 'required' || issue.message === 'email' || issue.message === 'url' || issue.message === 'number' || issue.message === 'min' || issue.message === 'max' || issue.message === 'pattern') return issue.message;
        if (issue.code === 'too_small' && issue.type === 'string' && issue.minimum === 1) return 'required';
        if (issue.code === 'invalid_format' && issue.format === 'email') return 'email';
        if (issue.code === 'invalid_format' && issue.format === 'url') return 'url';
        return issue.code;
    }

    /**
     * Maps Zod errors to the form fields and updates the UI.
     */
    displayErrors(form, error) {
        const issuesByField = error.issues.reduce((acc, issue) => {
            const fieldName = issue.path[0];
            if (!acc[fieldName]) acc[fieldName] = [];
            acc[fieldName].push(issue);
            return acc;
        }, {});

        for (const [fieldName, issues] of Object.entries(issuesByField)) {
            const allElements = Array.from(form.querySelectorAll(`[name="${fieldName}"]`));
            const primaryField = allElements[0] || form.querySelector(`#${fieldName}`);
            if (!primaryField) continue;

            // Apply is-invalid to ALL elements with this name (crucial for radio groups)
            allElements.forEach(el => el.classList.add('is-invalid'));
            
            const triggeredTypes = issues.map(issue => this.getErrorType(issue));
            const container = primaryField.closest('.has-validation') || primaryField.parentElement;
            const feedbacks = Array.from(container.querySelectorAll('.invalid-feedback'));

            feedbacks.forEach(fb => fb.style.setProperty('display', 'none', 'important'));

            const targetFeedback = feedbacks.find(fb => {
                const type = fb.getAttribute('data-error-type');
                return type && triggeredTypes.includes(type);
            });

            if (targetFeedback) {
                targetFeedback.style.setProperty('display', 'block', 'important');
            } else {
                const genericFeedback = feedbacks.find(fb => !fb.hasAttribute('data-error-type'));
                if (genericFeedback) {
                    genericFeedback.style.setProperty('display', 'block', 'important');
                    if (!genericFeedback.textContent.trim()) {
                        genericFeedback.textContent = issues[0].message;
                    }
                }
            }
        }
    }

    /**
     * Clears all validation errors from the form.
     */
    clearErrors(form) {
        form.classList.remove('was-validated');
        form.querySelectorAll('.is-invalid').forEach(el => el.classList.remove('is-invalid'));
        form.querySelectorAll('.invalid-feedback').forEach(fb => fb.style.display = '');
    }
}
